import { useState, useEffect } from "react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Link, useParams } from "react-router-dom";
import { Search, Package, Truck, CheckCircle2, Clock, XCircle, MapPin, ArrowLeft, ShoppingCart } from "lucide-react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL || '';

const STATUS_STEPS = [
  { key: "new", label: "Comanda plasata", icon: ShoppingCart },
  { key: "confirmed", label: "Confirmata", icon: CheckCircle2 },
  { key: "preparing", label: "In pregatire", icon: Package },
  { key: "out_for_delivery", label: "In livrare", icon: Truck },
  { key: "delivered", label: "Livrata", icon: CheckCircle2 },
];

const STATUS_INDEX = { new: 0, confirmed: 1, preparing: 2, out_for_delivery: 3, delivered: 4, cancelled: -1 };

export default function TrackOrderPage() {
  const { trackingCode: urlCode } = useParams();
  const [code, setCode] = useState(urlCode || "");
  const [order, setOrder] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleTrack = async (e) => {
    if (e) e.preventDefault();
    if (!code.trim()) return;
    setLoading(true);
    setError("");
    setOrder(null);
    try {
      const res = await axios.get(`${API}/api/public/track/${code.trim().toUpperCase()}`);
      setOrder(res.data);
    } catch {
      setError("Comanda nu a fost gasita. Verificati codul de tracking.");
    }
    setLoading(false);
  };

  useEffect(() => {
    if (urlCode) handleTrack();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlCode]);

  const currentStep = order ? STATUS_INDEX[order.status] : -1;
  const isCancelled = order?.status === "cancelled";

  return (
    <div className="min-h-screen bg-[#0a0a0b] text-white">
      {/* Header */}
      <header className="border-b border-white/10 px-4 py-4">
        <div className="max-w-3xl mx-auto flex items-center justify-between">
          <Link to="/boutique" className="flex items-center gap-2 text-sm text-gray-400 hover:text-white transition-colors">
            <ArrowLeft className="w-4 h-4" />
            Inapoi la magazin
          </Link>
          <Link to="/boutique" className="flex items-center gap-2">
            <img src="https://customer-assets.emergentagent.com/job_509254e3-4fbd-4a68-8194-b9dcfb6ff3e4/artifacts/bylh1er6_Image.jpg" alt="Scutersh" className="w-8 h-8 rounded object-contain" />
            <span className="font-bold text-sm">SCUTERSH</span>
          </Link>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-4 py-12">
        <div className="text-center mb-8">
          <h1 className="text-3xl sm:text-4xl font-bold mb-2" data-testid="track-title">Urmareste comanda</h1>
          <p className="text-gray-400">Introdu codul de tracking primit la plasarea comenzii</p>
        </div>

        {/* Search */}
        <form onSubmit={handleTrack} className="flex gap-3 max-w-md mx-auto mb-8">
          <Input
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder="Ex: JD-A1B2C3"
            className="bg-white/5 border-white/10 text-white placeholder:text-gray-500"
            data-testid="track-input"
          />
          <Button type="submit" disabled={loading} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="track-submit">
            <Search className="w-4 h-4 mr-2" />
            {loading ? "..." : "Cauta"}
          </Button>
        </form>

        {error && (
          <div className="text-center text-emerald-400 mb-4 flex items-center justify-center gap-2" data-testid="track-error">
            <XCircle className="w-4 h-4" />
            {error}
          </div>
        )}

        {/* Result */}
        {order && (
          <Card className="bg-white/5 border-white/10 animate-fade-in" data-testid="track-result">
            <CardContent className="p-6 space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-400">Cod tracking</p>
                  <p className="font-mono font-bold text-lg text-emerald-400">{order.tracking_code}</p>
                </div>
                {isCancelled ? (
                  <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30 border">Anulata</Badge>
                ) : (
                  <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30 border">
                    {STATUS_STEPS[currentStep]?.label || order.status}
                  </Badge>
                )}
              </div>

              {/* Progress steps */}
              {!isCancelled && (
                <div className="relative">
                  <div className="flex justify-between">
                    {STATUS_STEPS.map((step, i) => {
                      const done = i <= currentStep;
                      const active = i === currentStep;
                      const StepIcon = step.icon;
                      return (
                        <div key={step.key} className="flex flex-col items-center flex-1">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center border-2 transition-all ${
                            done ? "bg-emerald-600 border-emerald-600 text-white" :
                            "bg-transparent border-white/20 text-gray-500"
                          } ${active ? "ring-2 ring-emerald-600/50 ring-offset-2 ring-offset-[#0a0a0b]" : ""}`}>
                            <StepIcon className="w-4 h-4" />
                          </div>
                          <p className={`text-[10px] mt-2 text-center leading-tight ${done ? "text-white" : "text-gray-500"}`}>
                            {step.label}
                          </p>
                        </div>
                      );
                    })}
                  </div>
                  {/* Progress line */}
                  <div className="absolute top-5 left-[10%] right-[10%] h-0.5 bg-white/10 -z-0">
                    <div
                      className="h-full bg-emerald-600 transition-all duration-500"
                      style={{ width: `${Math.max(0, (currentStep / (STATUS_STEPS.length - 1)) * 100)}%` }}
                    />
                  </div>
                </div>
              )}

              {isCancelled && (
                <div className="text-center py-4">
                  <XCircle className="w-12 h-12 text-red-500 mx-auto mb-2" />
                  <p className="text-emerald-400 font-medium">Aceasta comanda a fost anulata</p>
                </div>
              )}

              {/* Order details */}
              <div className="grid grid-cols-2 gap-4 text-sm border-t border-white/10 pt-4">
                <div>
                  <p className="text-gray-400 text-xs">Produs</p>
                  <p className="font-medium">{order.item_name || "N/A"}</p>
                </div>
                <div>
                  <p className="text-gray-400 text-xs">Total</p>
                  <p className="font-bold text-emerald-400">{order.total_eur?.toFixed(0)} EUR</p>
                </div>
                {order.zone && (
                  <div>
                    <p className="text-gray-400 text-xs">Zona livrare</p>
                    <p className="flex items-center gap-1"><MapPin className="w-3 h-3" />{order.zone}</p>
                  </div>
                )}
                <div>
                  <p className="text-gray-400 text-xs">Cost livrare</p>
                  <p>{order.delivery_fee_eur} EUR</p>
                </div>
                <div>
                  <p className="text-gray-400 text-xs">Data comenzii</p>
                  <p>{order.created_at ? new Date(order.created_at).toLocaleDateString("ro-RO", { day: "numeric", month: "long", year: "numeric" }) : "—"}</p>
                </div>
                {order.updated_at && (
                  <div>
                    <p className="text-gray-400 text-xs">Ultima actualizare</p>
                    <p>{new Date(order.updated_at).toLocaleDateString("ro-RO", { day: "numeric", month: "long", year: "numeric" })}</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* WhatsApp contact */}
        <div className="text-center mt-8">
          <p className="text-gray-400 text-sm mb-3">Ai intrebari despre comanda ta?</p>
          <a
            href="https://wa.me/40792250058?text=Buna! Am o intrebare despre comanda mea."
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white px-5 py-2.5 rounded-full text-sm font-medium transition-colors"
            data-testid="track-whatsapp"
          >
            Contacteaza-ne pe WhatsApp
          </a>
        </div>
      </div>
    </div>
  );
}
